import { useMemo } from 'react';

import {
  AiAgentConfigCommit,
  AiAgentListItem,
  aiAgentsApi,
} from 'apis/aiAgents';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { askSidekick, SidekickChoice } from 'utils/sidekick';

import { AiAgentDraftVersion } from './sidekickDebugButton.utils';

const DRAFT = 'draft';

/**
 * Lists the versions to draft with, the live one picked by default, or the
 * draft without a deployed version, as the live agent then runs it. The draft
 * is the working copy, which is no version of its own, so Sidekick drafts with
 * the latest version for it.
 */
const getVersionChoices = (
  commits: readonly AiAgentConfigCommit[],
): { choices: SidekickChoice[]; defaultAnswer: string } => {
  const live = commits.find((commit) => commit.deployed);
  const versions = commits.map((commit) => ({
    value: commit.chatbotVersionId,
    label: `v${commit.displayVersion} ${commit.name}`,
    badge: commit.deployed ? 'Live' : undefined,
  }));

  return {
    choices: [
      { value: DRAFT, label: 'Draft', badge: live ? undefined : 'Live' },
      ...versions,
    ],
    defaultAnswer: live ? live.chatbotVersionId : DRAFT,
  };
};

/**
 * Finds the AI agent behind the first of the users that is one, when Sidekick
 * is enabled.
 */
export const useSidekickAiAgent = (
  userIds: readonly string[],
): AiAgentListItem | undefined => {
  const flags = useFeatureFlag(['mim-sidekick']);

  const { data } = aiAgentsApi.useReadAiAgentsListQuery(
    {},
    { skip: !flags.mimSidekick || userIds.length === 0 },
  );

  return useMemo(() => {
    if (!flags.mimSidekick) return undefined;
    const agents = data?.data?.agents ?? [];
    for (const userId of userIds) {
      const agent = agents.find((item) => item.systemUserId === userId);
      if (agent) return agent;
    }
    return undefined;
  }, [flags.mimSidekick, data, userIds]);
};

type AskSidekickToDraftOptions = {
  /** The id of the AI agent to draft with. */
  aiAgentId: string;
  /** The title of the modal. */
  title: string;
  /** Builds the prompt from the version picked. */
  createPrompt: (version: AiAgentDraftVersion) => string;
};

/**
 * Asks the user which version of an AI agent to draft a reply with before the
 * prompt is sent to Sidekick.
 */
export const useAskSidekickToDraft = (): ((
  options: AskSidekickToDraftOptions,
) => Promise<void>) => {
  const [readConfigCommits] =
    aiAgentsApi.useLazyReadAiAgentsConfigCommitsQuery();

  return async ({ aiAgentId, title, createPrompt }) => {
    // Drafting with the draft still works when the versions can't load.
    const commits = await readConfigCommits({ id: aiAgentId }, true)
      .unwrap()
      .then((response) => response.data.configCommits)
      .catch(() => []);

    askSidekick({
      title,
      label: 'Choose a version to draft with',
      ...getVersionChoices(commits),
      createPrompt: (answer) =>
        createPrompt({
          version: commits.find((commit) => commit.chatbotVersionId === answer),
        }),
    });
  };
};
